import React from 'react';
import { ShieldCheck, Lock, DatabaseBackup, Users, ScrollText, EyeOff } from 'lucide-react';
import MfaSetupCard from './MfaSetupCard';
import SessionsList from './SessionsList';

const PILLARS = [
  { icon: EyeOff, label: 'Patient identities walled off', testid: 'ycyd-pillar-walled' },
  { icon: Lock, label: 'AES-256 encryption at rest', testid: 'ycyd-pillar-aes' },
  { icon: DatabaseBackup, label: 'Daily encrypted backups', testid: 'ycyd-pillar-backups' },
  { icon: Users, label: 'Role-based access control', testid: 'ycyd-pillar-rbac' },
  { icon: ScrollText, label: 'Tamper-evident audit logs', testid: 'ycyd-pillar-audit' },
];

export default function SecurityPrivacyTab() {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-8" data-testid="settings-security-tab">
      <header className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
          <ShieldCheck size={24} className="text-emerald-600" />
        </div>
        <div className="min-w-0">
          <h1 className="font-[Manrope,Inter,sans-serif] font-extrabold text-2xl sm:text-3xl text-slate-900 tracking-tight" data-testid="ycyd-heading">
            YCYD — Vault Mode
          </h1>
          <p className="mt-1 text-base md:text-lg font-semibold text-emerald-700" data-testid="ycyd-subheading">
            Your Clinic, Your Data. Privacy by Design.
          </p>
        </div>
      </header>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4 text-[14px] text-slate-700 leading-relaxed" data-testid="ycyd-body">
        <p>
          Vault Mode is built into every AudiNexa account, ensuring patient identities — names, phone numbers, emails, and referring doctors — are strictly walled off from unauthorized eyes. AudiNexa staff cannot access them. The founder cannot see them. This security is enforced at the system level, not just written in a privacy policy.
        </p>
        <p>
          To protect your practice under Indian data laws, we secure your database with enterprise-grade AES-256 encryption at rest, automated daily encrypted backups, role-based access control (RBAC), and tamper-evident audit logs.
        </p>
      </section>

      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ycyd-pillars">
        {PILLARS.map(({ icon: Icon, label, testid }) => (
          <li key={testid} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] font-semibold text-slate-800" data-testid={testid}>
            <Icon size={16} className="text-emerald-600 shrink-0" /> {label}
          </li>
        ))}
      </ul>

      {/* Not part of Vault Mode — kept: 2FA is mandatory for platform admins. */}
      <section className="mt-10">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">
          Sign-in protection
        </div>
        <MfaSetupCard />
      </section>

      <section className="mt-10">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">
          Sessions &amp; devices
        </div>
        <SessionsList />
      </section>
    </div>
  );
}
